import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from './logo/immersia.png';
import './WelcomePage.css';
import SeasonalBanner from './SeasonalBanner';
import useEasterTheme from './hooks/useEasterTheme';
import { getEasterThemeClassName } from '../utils/easterTheme';
import ComingSoonModal, {
  claimComingSoonOncePerSession,
  isComingSoonEnabled,
} from './ComingSoonModal';
import { trackComingSoon } from '../utils/metaPixel';

const WelcomePage: React.FC = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const theme = useEasterTheme();
  const themeClassName = getEasterThemeClassName(theme.mode);
  const [showComingSoon, setShowComingSoon] = useState(false);

  /**
   * "Let's Play" shows the teaser once, then carries on into game selection —
   * and goes straight there on every later click of the same visit. Clearing
   * the env var skips the teaser entirely without touching this code.
   */
  const handlePlay = () => {
    if (isComingSoonEnabled && claimComingSoonOncePerSession()) {
      trackComingSoon();
      setShowComingSoon(true);
      return;
    }
    navigate('/GameSelection');
  };

  /**
   * Every way out of the teaser — "Got it", the close button, Escape, a click
   * on the backdrop — means the same thing: done watching, get on with it. So
   * they all land on the same page rather than stranding the visitor back on
   * Welcome depending on which one they happened to use.
   */
  const handleComingSoonClose = () => {
    setShowComingSoon(false);
    navigate('/GameSelection');
  };

  return (
    <div className={`welcome-page ${themeClassName}`.trim()}>
      <SeasonalBanner />
      <div className="content-wrapper">
        <div className="logo-container">
          <img src={logo} alt="Immersia Logo" className="logo" />
          <h1 className="title">IMMERSIA</h1>
          <p className="subtitle">Immersive Gaming Experiences</p>
          {theme.isActive && theme.mode && (
            <p className={`seasonal-note seasonal-note--${theme.mode}`}>
              <span className="seasonal-note__dot" aria-hidden="true" />
              <span>{theme.pageGreeting} {theme.pageAccent}</span>
            </p>
          )}
        </div>

        <div className="button-container">
          <button
            className="customer-button"
            onClick={handlePlay}
          >
            <span className="button-icon">🎮</span>
            Let's Play
          </button>
        </div>
      </div>

      {showComingSoon && <ComingSoonModal onClose={handleComingSoonClose} />}

      <footer className="welcome-footer">
        &copy; {currentYear} Immersia. All rights reserved.
      </footer>
    </div>
  );
};

export default WelcomePage;
